import { useState } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import type { TimelineEvent } from "../api/client";
import {
  KINDS,
  describe,
  timesOfInterest,
  type Relation,
  type Term,
} from "./events";

/** Shown at once; zoom in for the rest. */
const MAX_ROWS = 200;

const ANY = "";

const hms = (utcMs: number) => new Date(utcMs).toISOString().slice(11, 19);

function TermPicker({
  label,
  term,
  kinds,
  sources,
  onChange,
}: {
  label: string;
  term: Term;
  kinds: string[];
  sources: string[];
  onChange: (t: Term) => void;
}) {
  return (
    <>
      <TextField
        select
        size="small"
        label={label}
        value={term.kind}
        onChange={(e) => onChange({ ...term, kind: e.target.value })}
        sx={{ minWidth: 170 }}
      >
        {kinds.map((k) => (
          <MenuItem key={k} value={k}>
            {KINDS[k] ?? k}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={`${label}: source`}
        value={term.source ?? ANY}
        slotProps={{
          inputLabel: { shrink: true },
          select: { displayEmpty: true },
        }}
        onChange={(e) => onChange({ ...term, source: e.target.value || null })}
        sx={{ minWidth: 170 }}
      >
        <MenuItem value={ANY}>Any source</MenuItem>
        {sources.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </TextField>
    </>
  );
}

/**
 * The times of interest in view (design §8.4), or those matching a combined
 * query such as speech while a van is stationary; a row plays from its time.
 */
export function EventsList({
  events,
  all,
  onSeek,
}: {
  /** Events to list. */
  events: TimelineEvent[];
  /** Those and the context (GPS moving and stationary, camera moving). */
  all: TimelineEvent[];
  onSeek: (utcMs: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [combined, setCombined] = useState(false);
  const [find, setFind] = useState<Term>({ kind: "speech", source: null });
  const [relation, setRelation] = useState<Relation>("while");
  const [during, setDuring] = useState<Term>({
    kind: "stationary",
    source: null,
  });
  const [words, setWords] = useState("");
  const count = (...kinds: string[]) =>
    events.filter((e) => kinds.includes(e.kind)).length;
  const kinds = Object.keys(KINDS).filter((k) => all.some((e) => e.kind === k));
  const sources = [
    ...new Set(all.flatMap((e) => (e.source ? [e.source] : []))),
  ].sort();
  const wanted = words.trim().toLowerCase();
  const rows = (
    combined
      ? timesOfInterest(all, find, relation, during)
      : events.map((e) => ({
          from: Date.parse(e.start_utc),
          to: Date.parse(e.end_utc),
          event: e,
        }))
  ).filter(
    ({ event: e }) =>
      !wanted ||
      [e.text, e.translation].some((t) => t?.toLowerCase().includes(wanted)),
  );
  return (
    <>
      <Button
        size="small"
        sx={{ mt: 2 }}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {open ? "Hide" : "Show"} events: {count("speech", "said")} speech,{" "}
        {count("motion")} motion, {count("loud")} loud
      </Button>
      {open && (
        <>
          <Stack
            direction="row"
            sx={{ gap: 1.5, flexWrap: "wrap", alignItems: "center", my: 1 }}
            role="group"
            aria-label="Combined query"
          >
            <TextField
              size="small"
              label="Words"
              value={words}
              onChange={(e) => setWords(e.target.value)}
              placeholder="What was said"
              sx={{ minWidth: 170 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={combined}
                  onChange={(e) => setCombined(e.target.checked)}
                />
              }
              label="Only"
            />
            <TermPicker
              label="Find"
              term={find}
              kinds={kinds}
              sources={sources}
              onChange={setFind}
            />
            <TextField
              select
              size="small"
              label="When"
              value={relation}
              onChange={(e) => setRelation(e.target.value as Relation)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="while">while</MenuItem>
              <MenuItem value="while_not">while not</MenuItem>
            </TextField>
            <TermPicker
              label="During"
              term={during}
              kinds={kinds}
              sources={sources}
              onChange={setDuring}
            />
          </Stack>
          <Table size="small" aria-label="Events">
            <TableHead>
              <TableRow>
                <TableCell>Time (UTC)</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Event</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, MAX_ROWS).map(({ from, to, event: e }) => (
                <TableRow
                  key={`${e.kind}${e.file_id}${e.start_utc}${from}`}
                  hover
                  onClick={() => onSeek(from)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    <Button
                      size="small"
                      sx={{ fontFamily: "monospace", p: 0, minWidth: 0 }}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onSeek(from);
                      }}
                      aria-label={`Play from ${hms(from)}`}
                    >
                      {hms(from)}
                    </Button>
                    {combined && `–${hms(to)}`}
                  </TableCell>
                  <TableCell>{e.source ?? "Source not identified"}</TableCell>
                  <TableCell>{describe(e)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>Nothing in view matches.</TableCell>
                </TableRow>
              )}
              {rows.length > MAX_ROWS && (
                <TableRow>
                  <TableCell colSpan={3}>
                    {rows.length - MAX_ROWS} more: zoom in to see them.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}
    </>
  );
}
